import * as React from "react";
import { Link, useHistory } from "react-router-dom";
import {
  PrimaryButton,
  SecondaryButton,
  RadioButton,
  Select,
  ExternalLink,
  TextField,
} from "@graffer-inc/console-design-kit";
import { IFormula } from "@graffer-inc/form-schema";
import { InvoiceItemSetting } from "@graffer-inc/cloud-gov-core";
import { useEditModal } from "./use-edit-modal";
import { BaseModal } from "../../../../../atoms/base-modal";
import { ResourceFetchError } from "../../../../common/resource-fetch-error";

export type PaymentSettingEditModalProps = {
  formulaEditHref: string;
  formulas: IFormula[];
  initItem?: InvoiceItemSetting;
  onSave: (item: InvoiceItemSetting) => void;
  onClose: () => void;
};

export const PaymentSettingEditModal: React.FC<
  PaymentSettingEditModalProps
> = ({ formulaEditHref, formulas, initItem, onSave, onClose }) => {
  const history = useHistory();
  const { touched, setTouched, item, setItem, error } = useEditModal(initItem);

  return (
    <BaseModal
      isActive
      title={`決済設定の${initItem ? "編集" : "追加"}`}
      onClose={onClose}
    >
      {formulas.length > 0 ? (
        <section className="modal-card-body">
          <div className="field">
            <TextField
              value={item.description}
              errorMessages={error.description}
              onChange={(e) =>
                setItem((oldValue) => ({
                  ...oldValue,
                  description: e.target.value,
                }))
              }
              onBlur={() =>
                setTouched((oldValue) => ({
                  ...oldValue,
                  description: true,
                }))
              }
              required
              label={"費目名"}
              description={"決済の費目名を入力してください。"}
              placeholder={""}
            />
          </div>
          <div className="field">
            <RadioButton
              value={item.formulaId}
              options={formulas.map((f) => ({
                name: f.id,
                value: f.id,
                description:
                  f.formula.length > 55
                    ? f.formula.slice(0, 55).concat("...")
                    : f.formula,
              }))}
              errorMessages={
                !!error.formulaId && touched.formulaId
                  ? error.formulaId
                  : undefined
              }
              onChange={(e) => {
                setItem((oldValue) => ({
                  ...oldValue,
                  formulaId: e.target.value,
                }));
                setTouched((oldValue) => ({
                  ...oldValue,
                  formulaId: true,
                }));
              }}
              label={"自動計算数式"}
              description={"決済に使用する自動計算数式を選択してください。"}
              required
            />
            <div className="columns mt-2">
              <p className="column has-text-left">
                <ExternalLink
                  anchor={({ ...props }) => (
                    <Link {...props} to={formulaEditHref}>
                      <span>自動計算数式の詳細はこちら</span>
                    </Link>
                  )}
                >
                  <span>
                    自動計算数式を{formulas.length > 0 ? "編集" : "作成"}する
                  </span>
                </ExternalLink>
              </p>
            </div>
          </div>
          <div className="field">
            <Select
              value={item.taxRate}
              options={[
                { value: "0", name: "非課税" },
                { value: "0.1", name: "10%" },
                { value: "0.08", name: "8%" },
              ]}
              onChange={(v) => {
                setItem((oldValue) => ({
                  ...oldValue,
                  taxRate: v,
                }));
                setTouched((oldValue) => ({
                  ...oldValue,
                  taxRate: true,
                }));
              }}
              label={"税率"}
              description={"決済にかかる税率を設定してください。"}
              required
            />
          </div>
        </section>
      ) : (
        <section className="modal-card-body">
          <div className="field">
            <label className="label">自動計算数式</label>
            <ResourceFetchError
              faIcon="fa-calculator"
              title=""
              subtitle="自動計算数式が設定されていません。"
            />
          </div>
        </section>
      )}
      <footer className="columns modal-card-foot is-centered">
        <div className="column is-3">
          <SecondaryButton text="キャンセル" onClick={onClose} />
        </div>
        {formulas.length > 0 ? (
          <div className="column is-3">
            <PrimaryButton
              text="保存"
              onClick={() => onSave(item)}
              disabled={Object.values(error).some((k) => k)}
            />
          </div>
        ) : (
          <div className="column is-3">
            <PrimaryButton
              text="自動計算数式を作成する"
              onClick={() => history.push(formulaEditHref)}
            />
          </div>
        )}
      </footer>
    </BaseModal>
  );
};
